import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DEPARTMENT_MAP, MAX_ORG_DEPTH } from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { api } from '../../lib/api';
import { num, plural, WORDS } from '../../lib/format';

interface OrgNode {
  id: string;
  title: string;
  deptKey: string | null;
  department: string | null;
  gradeName: string | null;
  gradeLevel: number | null;
  reportsToId: string | null;
  headcount: number;
  holders: { id: string; name: string }[];
  children: OrgNode[];
}

interface OrgTree {
  roots: OrgNode[];
  total: number;
  unassigned: number;
  withoutPosition: number;
}

/** صفٌّ مسطَّحٌ من الشجرة — العمقُ محفوظٌ معه ليُزاح به الصفُّ عند العرض. */
interface FlatRow {
  node: OrgNode;
  depth: number;
}

/**
 * الهيكل التنظيمي.
 *
 * الشجرةُ تُرسم بالوظائف لا بالأشخاص. الفرقُ ليس شكليًّا: هيكلٌ مرسومٌ
 * بالأشخاص يتغيّر شكلُه كلّما استقال أحدٌ أو نُقل، فيصير الهيكلُ صورةً
 * لمن بقي لا لبنيةِ الشركة. أمّا الوصفُ الوظيفيُّ فيبقى بعد صاحبه،
 * والأشخاصُ يُعلَّقون عليه ويُنزعون عنه دون أن تتغيّر الشجرة.
 *
 * ولذلك يظهر «شاغر» صريحًا: وظيفةٌ بلا من يشغلها حقيقةٌ في الهيكل
 * تستحقّ أن تُرى، لا فراغٌ يُطوى.
 */
export function OrgChart() {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const tree = useQuery({
    queryKey: ['hr', 'org', 'tree'],
    queryFn: () => api<OrgTree>('/hr/org/tree'),
  });

  const roots = tree.data?.roots ?? [];

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = flatten(roots, collapsed);
  const sum = summarize(roots);
  const branches = branchIds(roots);

  /*
   * جذرٌ يتبع أحدًا ليس جذرًا حقيقيًّا: الشجرةُ تُبنى من الوظائف المفعَّلة
   * وحدها، فوظيفةٌ رئيسُها موقوفٌ تُدفع إلى الجذر كأنّها بلا رئيس.
   */
  const orphans = roots.filter((r) => r.reportsToId !== null);

  /*
   * فرقٌ يُقال لا يُخفى: الخادمُ يعدّ كلّ موظّفٍ مربوطٍ بوصفٍ وظيفيّ،
   * والشجرةُ لا تعرض إلّا المربوطين بوصفٍ مفعَّل. ما بينهما موظّفون
   * وصفُهم موقوف — لا هم في الشجرة ولا هم في عدّاد «بلا وظيفة».
   */
  const onStoppedPositions = Math.max((tree.data?.unassigned ?? 0) - sum.held, 0);

  return (
    <div className="page">
      <PageHead
        title="الهيكل التنظيمي"
        subtitle="شجرةٌ من الوظائف لا من الأشخاص — لأنّ هيكلًا يُرسم بالأشخاص يتغيّر شكلُه باستقالةِ واحد، وبنيةُ الشركة لا تتغيّر"
        actions={
          <Link className="btn" to="/hr/org/positions">
            <Icon name="clipboard" size={15} />
            الأوصاف الوظيفية
          </Link>
        }
      />

      {tree.isError && (
        <ErrorState error={tree.error} onRetry={() => void tree.refetch()} />
      )}

      {tree.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {tree.data && tree.data.total > 0 && (
        <div className="stats">
          <Stat label="وظائف مفعّلة" value={num(tree.data.total)} />
          <Stat
            label="وظائف شاغرة"
            value={num(sum.vacant)}
            note={sum.vacant > 0 ? 'لا أحدَ يشغلها اليوم' : undefined}
          />
          <Stat label="موظّفون على الشجرة" value={num(sum.held)} />
          <Stat
            label="بلا وصف وظيفي"
            value={num(tree.data.withoutPosition)}
            note={
              tree.data.withoutPosition > 0 ? 'خارج الشجرة كلّها' : undefined
            }
          />
        </div>
      )}

      {tree.data && tree.data.total > 0 && (
        <Notice title="العقدةُ وظيفةٌ، ومن يشغلها معلَّقٌ عليها" icon="info">
          العقدةُ هنا وصفٌ وظيفيٌّ ثابت، واسمُ الموظّف تحته إجابةُ «من يشغلها
          اليوم». فإن استقال ظهرت الوظيفةُ شاغرةً ولم تتغيّر الشجرة — وهذا
          هو المقصود: الهيكلُ يصف الشركة، لا من بقي فيها.
        </Notice>
      )}

      {orphans.length > 0 && (
        <Notice title="وظائفُ رئيسُها خارج الشجرة" icon="alert" kind="warning">
          {plural(orphans.length, WORDS.job)} تتبع وظيفةً موقوفة، فظهرت في
          الجذر كأنّها بلا رئيس. الشجرةُ لا تُبنى إلّا من المفعَّل — وتصحيحُها
          بتغيير «تتبع» في وصفها أو بإعادة تفعيل رئيسها.
        </Notice>
      )}

      {onStoppedPositions > 0 && (
        <Notice title="موظّفون على أوصافٍ موقوفة" icon="alert" kind="warning">
          {num(onStoppedPositions)} من الموظّفين مربوطون بوصفٍ وظيفيٍّ موقوف،
          فلا يظهرون في الشجرة ولا يُحسبون في «بلا وصف وظيفي». أعِد تفعيل
          الوصف أو انقلهم إلى وصفٍ مفعَّل حتّى يُرى موضعُهم.
        </Notice>
      )}

      {sum.deepest >= MAX_ORG_DEPTH && (
        <Notice title="الشجرةُ مقطوعةٌ عند حدٍّ" icon="alert" kind="warning">
          الخادمُ يقف عند {num(MAX_ORG_DEPTH)} مستوًى حمايةً من حلقةٍ لا تنتهي
          في البيانات. ما تحت هذا العمق موجودٌ في الأوصاف الوظيفية ولا يظهر
          في هذه الشجرة.
        </Notice>
      )}

      {branches.length > 0 && (
        <div className="toolbar">
          <button
            className="btn btn-sm"
            onClick={() => setCollapsed(new Set(branches))}
          >
            <Icon name="chevronUp" size={14} />
            طيّ الكل
          </button>
          <button className="btn btn-sm" onClick={() => setCollapsed(new Set())}>
            <Icon name="chevronDown" size={14} />
            توسيع الكل
          </button>
        </div>
      )}

      {tree.data && tree.data.total === 0 && (
        <EmptyState
          title="لا هيكل بعد"
          text="الهيكلُ يُرسم بالأوصاف الوظيفية لا بأسماء الموظّفين. اكتب أوّل وصفٍ وحدِّد من يتبع من، وتنمو الشجرةُ من نفسها."
          action={
            <Link className="btn btn-primary" to="/hr/org/positions">
              اكتب أوّل وصف وظيفي
            </Link>
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>الوظيفة</th>
                  <th>القسم</th>
                  <th>الدرجة</th>
                  <th>من يشغلها</th>
                  <th>العدد المعتمَد</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ node, depth }) => {
                  const hasChildren = node.children.length > 0;
                  const isCollapsed = collapsed.has(node.id);
                  const dep = node.deptKey ? DEPARTMENT_MAP[node.deptKey] : null;
                  const deptLabel = node.department ?? dep?.label ?? node.deptKey;
                  /* رئيسٌ مذكورٌ في وصفها ومع ذلك هي في الجذر — أي رئيسٌ موقوف. */
                  const orphan = depth === 1 && node.reportsToId !== null;

                  return (
                    <tr key={node.id}>
                      <td>
                        {/* الإزاحةُ بالبداية المنطقية لا باليسار: الشجرةُ تُقرأ من اليمين. */}
                        <span
                          className="row"
                          style={{ paddingInlineStart: (depth - 1) * 18 }}
                        >
                          {hasChildren ? (
                            <button
                              className="btn btn-icon btn-ghost btn-sm"
                              onClick={() => toggle(node.id)}
                              aria-label={isCollapsed ? 'توسيع' : 'طي'}
                              aria-expanded={!isCollapsed}
                            >
                              <Icon
                                name={isCollapsed ? 'chevronLeft' : 'chevronDown'}
                                size={14}
                              />
                            </button>
                          ) : (
                            <span style={{ width: 26 }} />
                          )}
                          <Link
                            to={`/hr/org/positions/${node.id}`}
                            className="cell-strong"
                          >
                            {node.title}
                          </Link>
                          {hasChildren && isCollapsed && (
                            <span className="tiny mute">
                              {plural(node.children.length, WORDS.job)} تحتها
                            </span>
                          )}
                          {orphan && (
                            <span className="tiny mute">رئيسُها موقوف</span>
                          )}
                        </span>
                      </td>

                      <td>
                        {deptLabel ? (
                          <Badge kind={dep?.kind ?? 'neutral'}>{deptLabel}</Badge>
                        ) : (
                          <span className="cell-mute">—</span>
                        )}
                      </td>

                      <td className="cell-mute">{node.gradeName ?? '—'}</td>

                      <td>
                        {node.holders.length === 0 ? (
                          /* الشاغرُ يُقال لا يُترك فراغًا: الفراغُ يُقرأ خطأً في البيانات. */
                          <Badge kind="warning">شاغر</Badge>
                        ) : (
                          <span className="row" style={{ flexWrap: 'wrap' }}>
                            {node.holders.map((h, i) => (
                              <span key={h.id}>
                                {i > 0 && <span className="mute">، </span>}
                                <Link to={`/hr/employees/${h.id}`}>{h.name}</Link>
                              </span>
                            ))}
                          </span>
                        )}
                      </td>

                      <td className="num">
                        {node.headcount > 1 ? (
                          <>
                            {num(node.headcount)}
                            {node.holders.length !== node.headcount && (
                              <span className="tiny mute">
                                {' '}
                                ({num(node.holders.length)} مشغول)
                              </span>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tree.data && (
        <p className="tiny mute mt-2">
          {tree.data.withoutPosition > 0 ? (
            <>
              {num(tree.data.withoutPosition)} من الموظّفين على رأس العمل لا
              وصفَ وظيفيَّ لهم، فلا موضعَ لهم في هذه الشجرة. هذا نقصٌ في
              البيانات لا في الهيكل، ويُسدّ باختيار وصفٍ لكلٍّ منهم من{' '}
              <Link to="/hr/employees">شاشة الموظّفين</Link>.
            </>
          ) : (
            'كلُّ موظّفٍ على رأس العمل له وصفٌ وظيفيّ — لا أحدَ خارج الشجرة.'
          )}
        </p>
      )}
    </div>
  );
}

/**
 * تسطيحُ الشجرة للعرض.
 *
 * يتوقّف عند المطويّ فلا يُدرج أبناءه — والطيُّ إخفاءُ عرضٍ لا حذف،
 * ولذلك لا تمرّ الإحصاءاتُ من هنا.
 */
function flatten(
  nodes: OrgNode[],
  collapsed: Set<string>,
  depth = 1,
): FlatRow[] {
  const out: FlatRow[] = [];
  for (const node of nodes) {
    out.push({ node, depth });
    if (!collapsed.has(node.id)) {
      out.push(...flatten(node.children, collapsed, depth + 1));
    }
  }
  return out;
}

/**
 * إحصاءُ الشجرة كلّها — لا يعرف الطيّ.
 *
 * عددٌ يتغيّر حين يطوي أحدٌ فرعًا عددٌ كاذب: الطيُّ قرارُ نظرٍ لا واقعة.
 */
function summarize(
  nodes: OrgNode[],
  depth = 1,
): { vacant: number; held: number; deepest: number } {
  let vacant = 0;
  let held = 0;
  let deepest = nodes.length > 0 ? depth : depth - 1;

  for (const node of nodes) {
    if (node.holders.length === 0) vacant += 1;
    held += node.holders.length;
    const sub = summarize(node.children, depth + 1);
    vacant += sub.vacant;
    held += sub.held;
    if (sub.deepest > deepest) deepest = sub.deepest;
  }

  return { vacant, held, deepest };
}

/** معرّفاتُ كلّ ما له أبناء — يحتاجها زرُّ «طيّ الكل». */
function branchIds(nodes: OrgNode[], into: string[] = []): string[] {
  for (const node of nodes) {
    if (node.children.length > 0) {
      into.push(node.id);
      branchIds(node.children, into);
    }
  }
  return into;
}
